import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React, { useState } from 'react'
import { CampaignDetailsModal } from '../../../../components/CampaignDetailsModal'
import { useAuth } from '../../../../contexts/AuthContext'
import { useCampaigns } from '../../../../hooks/useCampaigns'
import { useClientProfile } from '../../../../hooks/useClientProfile'
import { AppLayout } from '../../../../components/layouts/AppLayout'
import BuyCreditsModal from '../../../../components/modals/BuyCreditsModal'
import InvoicesModal from '../../../../components/modals/InvoicesModal'

// Valid tab names for the campaign details modal
type DetailsTab = 'brief' | 'leads' | 'audience' | 'execution' | 'offer' | 'notes' | 'admin'

export const Route = createFileRoute('/campaigns/$campaignId/details')({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: (['brief', 'leads', 'audience', 'execution', 'offer', 'notes', 'admin'].includes(search.tab as string)
      ? search.tab
      : 'brief') as DetailsTab,
  }),
  component: CampaignDetailsPage,
})

function CampaignDetailsPage() {
  const navigate = useNavigate()
  const { campaignId } = Route.useParams()
  const { tab } = Route.useSearch()
  const { currentUser, targetEmail: impersonatedEmail } = useAuth()
  // Use impersonated email if set (admin viewing as another user), otherwise current user's email
  const targetEmail = impersonatedEmail || currentUser?.email
  const clientQuery = useClientProfile(targetEmail)
  const campaignsQuery = useCampaigns(targetEmail)

  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false)
  const [isInvoicesModalOpen, setIsInvoicesModalOpen] = useState(false)

  // Find the campaign metadata
  const campaignMetadata = campaignsQuery.data?.find((c: any) => c.campaignId === campaignId) || null

  const handleClose = () => {
    navigate({
      to: '/campaigns/$campaignId',
      params: { campaignId },
    })
  }

  return (
    <AppLayout
      currentView="campaigns"
      onBuyCredits={() => setIsBuyCreditsModalOpen(true)}
      onViewInvoices={() => setIsInvoicesModalOpen(true)}
      aiCredits={clientQuery.data?.credits ?? 0}
      creditsLoading={clientQuery.isPending}
      clientInfo={clientQuery.data}
    >
      {/* Campaign Details Modal */}
      {campaignMetadata && (
        <CampaignDetailsModal
          campaign={campaignMetadata}
          activeTab={tab}
          onTabChange={(next) =>
            navigate({
              to: '/campaigns/$campaignId/details',
              params: { campaignId },
              search: { tab: next as DetailsTab },
              replace: true,
            })
          }
          onClose={handleClose}
        />
      )}

      {/* Loading state if campaign not found */}
      {!campaignMetadata && !campaignsQuery.isPending && (
        <div className="min-h-screen flex items-center justify-center pt-28">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-white mb-2">Campaign not found</h2>
            <button
              onClick={() => navigate({ to: '/campaigns' })}
              className="mt-4 px-6 py-2 bg-[#5260FF] hover:bg-[#5260FF]/80 text-white rounded-lg text-sm font-medium"
            >
              Back to Campaigns
            </button>
          </div>
        </div>
      )}

      {/* Buy Credits Modal */}
      <BuyCreditsModal isOpen={isBuyCreditsModalOpen} onClose={() => setIsBuyCreditsModalOpen(false)} />

      {/* Invoices Modal */}
      <InvoicesModal isOpen={isInvoicesModalOpen} onClose={() => setIsInvoicesModalOpen(false)} />
    </AppLayout>
  )
}
